import React, { useCallback, useEffect, useState } from "react";
import { IoChevronDown, IoChevronUp, IoAttachOutline } from "react-icons/io5";
import Button from "../../component/button/Button";
import { creatorSupportTickets } from "../../api/HttpApis";
import style from "./CreatorSupport.module.css";

const STATUS_LABELS = { open: "Open", in_progress: "In Progress", resolved: "Resolved" };
const STATUS_CLASS = {
  open: style.statusOpen,
  in_progress: style.statusProgress,
  resolved: style.statusResolved,
};
const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");
const LIMIT = 10;

/**
 * The creator's own Help & Support tickets. The backend only returns this
 * creator's rows and never the admin's internal note.
 */
const MyRequests = ({ onNewRequest }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const res = await creatorSupportTickets({ page, limit: LIMIT });
      const payload = res?.data?.data ?? {};
      setRows(Array.isArray(payload.data) ? payload.data : []);
      setTotal(payload.total || 0);
    } catch {
      setFailed(true);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <div className={style.listState}>Loading your requests…</div>;

  if (failed) {
    return (
      <div className={style.listState}>
        Couldn't load your requests.{" "}
        <button type="button" className={style.linkBtn} onClick={load}>
          Try again
        </button>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className={style.listState}>
        <p>You haven't sent any support requests yet.</p>
        <Button variant="outline" onClick={onNewRequest}>
          Send a request
        </Button>
      </div>
    );
  }

  const lastPage = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <>
      <ul className={style.list}>
        {rows.map((t) => {
          const expanded = openId === t.id;
          // A common-problem ticket's subject is its category label; only
          // show the subject line when the creator typed their own.
          const ownSubject = t.subject !== t.category_label;
          return (
            <li key={t.id} className={style.item}>
              <button
                type="button"
                className={style.itemHead}
                onClick={() => setOpenId(expanded ? null : t.id)}
                aria-expanded={expanded}
              >
                <span className={style.itemNo}>#{t.id}</span>
                <span className={style.itemMain}>
                  <span className={style.itemTitle}>{ownSubject ? t.subject : t.category_label}</span>
                  <span className={style.itemSub}>
                    {ownSubject ? `${t.category_label} · ` : ""}
                    {fmtDate(t.created_at)}
                  </span>
                </span>
                <span className={`${style.status} ${STATUS_CLASS[t.status] || ""}`}>
                  {STATUS_LABELS[t.status] || t.status}
                </span>
                {expanded ? <IoChevronUp className={style.chev} /> : <IoChevronDown className={style.chev} />}
              </button>
              {expanded && (
                <div className={style.itemBody}>
                  <div className={style.itemMessage}>{t.message}</div>
                  {Array.isArray(t.attachments) && t.attachments.length > 0 && (
                    <div className={style.attachLinks}>
                      {t.attachments.map((a) => (
                        <a
                          key={a.url}
                          href={a.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={style.attachLink}
                        >
                          <IoAttachOutline />
                          {a.name}
                        </a>
                      ))}
                    </div>
                  )}
                  <div className={style.itemMeta}>
                    {t.status === "resolved"
                      ? `Resolved ${fmtDate(t.resolved_at)}. Check your email for our reply.`
                      : t.status === "in_progress"
                      ? "Our team is working on this. We'll reply by email."
                      : "Received. Our team will reply by email."}
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {total > LIMIT && (
        <div className={style.pager}>
          <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Prev
          </Button>
          <span>
            Page {page} of {lastPage}
          </span>
          <Button variant="outline" disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </div>
      )}
    </>
  );
};

export default MyRequests;
